import axios from '../../common/utils/interceptor';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiUrls, routeMappings } from '../../constants';
import { appendCdnToImagePath } from '../../common/utils/appendCdnToImagePath';
import { BaseStyles } from '../styles/base';
import { EXPERIENCE_HOST_DOMAINS } from '../../constants';
import { faChevronDown, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getCookie, getCurrentLocale } from '../../common/utils/cookies';
import { getExperienceInitCookie } from '../../common/utils/experienceInitCookie';
import { HeaderLangGroupProps } from '../../base/interfaces/headerInterface';
import { IELink } from '../../common/components/IELink';
import { LanguageDropdown } from '../../common/components/LanguageDropdown';
import { Logo } from './Logo';
import { logoutUser } from '../../common/utils/logoutUser';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { MobileHeader } from './MobileHeader';
import { ReactComponent as LanguageIcon } from '../../base/images/language_icon.svg';
import { SessionStore } from '../../constants/sessionStore';
import { TemplateType, TemplateUtilityLink } from '../../store/interfaces';
import { useBasicDetails } from '../../store/storeApi';
import { useTranslation } from 'react-i18next';
import { UtilityLinkStyles } from '../styles/utilityLinks';
import {
  AppBar,
  Grid,
  useMediaQuery,
  useTheme,
  MenuItem,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuList,
  InputLabel,
  Box,
  Link,
  Tooltip,
  Button,
  CircularProgress,
  Typography,
  Icon,
} from '@material-ui/core';
import AccessibilityDropdown from './AccessibilityDropdown';
import { useAppSelector } from '../../redux-store/hook';
import { headerDetailsProvider } from '../../redux-store/providers/headerDetailsProvider';

export const getLinksJSX = (links: Array<TemplateUtilityLink>): Array<JSX.Element> =>
  links
    .filter((link) => link.url && link.label)
    .map((link) => (
      <Box display="flex" key={link.label} component="li">
        {/* PopperProps and id for nvda issues CAW-1107 */}
        <Tooltip title={link.label} PopperProps={{ 'aria-hidden': true }} id="">
          <Link href={link.url} target={'_blank'} variant="body2" title="">
            <IELink>{link.label.length > 25 ? `${link.label.substring(0, 25)}...` : link.label}</IELink>
          </Link>
        </Tooltip>
      </Box>
    ));

/** Header Component */
const SHLEHeader = (): JSX.Element => {
  const headerDetails = useAppSelector((state) => state.headerDetails);
  const { updateHeaderDetails } = headerDetailsProvider();
  const { userSessionActive } = useAppSelector((state) => state.basicDetails);
  const template = useAppSelector((state) => state.template);
  const candidate = useAppSelector((state) => state.candidate);
  const { settings } = template;
  const { links, utilityBar } = settings;
  const theme = useTheme();
  const lgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const { t } = useTranslation();
  const history = useHistory();
  const classes = BaseStyles(settings);
  const utilityLinkClass = UtilityLinkStyles(utilityBar);

  const location = useLocation();
  const renderLanguageSelector = useMemo(() => {
    const matchPathsToNotRenderLanguageSelector = [
      {
        path: `${routeMappings['MFS']}${routeMappings['ACCOUNT']}`,
        exact: true,
      },
    ].map((pathConfig) => matchPath(location.pathname, pathConfig));

    return !matchPathsToNotRenderLanguageSelector.some((value) => value !== null);
  }, [location]);

  const workflowSession = sessionStorage.getItem(SessionStore.workflowSession);
  const parsedWorkflowSession = workflowSession && JSON.parse(workflowSession);
  const selectedAccount = sessionStorage.getItem(SessionStore.selectedAccountId) || 0;

  const tcPlusFlow = getCookie('TcPlusFlag') === '1';
  const basicFlow = template.type === TemplateType.BASIC_EXPERIENCE_LITE;

  let companyLogo = '';
  if (settings.utilityBar.logo) {
    companyLogo = settings.utilityBar.logo.base64logo
      ? settings.utilityBar.logo.base64logo
      : appendCdnToImagePath(settings.utilityBar.logo.path) || '';
  } else if (basicFlow) {
    companyLogo = sessionStorage.getItem('companyLogoUrl') || '';
  }

  const [headerDataLoading, setHeaderDataLoading] = useState<boolean>(false);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [languages, setLanguages] = useState<HeaderLangGroupProps[]>([]);

  // Deadline date passed
  const expCookie = getExperienceInitCookie();
  const deadlineDatePassed = (expCookie && expCookie.candidateDeadlinePassed) || false;

  // Preview Project
  const isPreviewProject = (expCookie && expCookie.previewProject) || false;

  const isPathShl360 = location.pathname.includes(`${routeMappings['MFS']}${routeMappings['LOGIN']}`);

  const ifErrorPage = (): boolean => {
    return (
      location.pathname.includes(routeMappings['EXPIRED_LINK']) ||
      location.pathname.includes(routeMappings['LINK_INVALID']) ||
      location.pathname.includes(routeMappings['NOT_FOUND']) ||
      location.pathname.includes(routeMappings['SECURE_BROWSER'])
    );
  };

  const fetchLanguages = useCallback(async (): Promise<void> => {
    if (!selectedAccount) {
      return;
    }

    try {
      const { data: languages } = await axios.get(
        apiUrls.GET_ACCOUNT_LANGUAGES.replace('{accountId}', `${selectedAccount}`),
      );

      setLanguages(languages);
      sessionStorage.setItem(SessionStore.mfsLanguages, JSON.stringify(languages));
    } catch (error) {
      console.error('error fetching MFS language data');
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (settings.mfsEnabled) {
      fetchLanguages();
    }
  }, [fetchLanguages, settings.mfsEnabled]);

  /** Fetch header data */
  useEffect(() => {
    const sessionLanguages = sessionStorage.getItem(SessionStore.mfsLanguages);
    const langs = sessionLanguages ? JSON.parse(sessionLanguages) : headerDetails.langGroups;

    setLanguages(langs);
    const getHeaderDetails = async (): Promise<void> => {
      try {
        setHeaderDataLoading(true);
        const companyId = expCookie && expCookie.companyId;
        const requestBody = companyId ? { companyId } : {};
        const { data: responseData } = await axios.post(apiUrls.POST_HEADER_DETAILS, requestBody);

        if (responseData.IsError) {
          console.error('error in fetching header data', responseData);
        } else {
          // modifying header data for integrations case
          const isIntegrated = sessionStorage.getItem(SessionStore.isIntegrated);

          if (isIntegrated && isIntegrated === 'true') {
            responseData.Data.showDetails = false;
          } else {
            responseData.Data.showDetails = true;
          }
          if (ifErrorPage()) {
            responseData.Data.langGroups = responseData.Data.langGroups.filter(
              (langObj: HeaderLangGroupProps) => langObj.locale.toUpperCase() === getCurrentLocale().toUpperCase(),
            );
          }
          sessionStorage.setItem(SessionStore.isSHLAppEnabled, JSON.stringify(responseData.Data.isShlAppEnabled));
          updateHeaderDetails(responseData.Data);
        }
      } catch (e) {
        console.error('error in getting header details', e);
      } finally {
        setHeaderDataLoading(false);
      }
    };

    if (!tcPlusFlow && headerDetails.langGroups && !headerDetails.langGroups.length && template.id) {
      getHeaderDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerDetails.langGroups, template.id]);

  // fetch navigation languages for tcPlus flow
  useEffect(() => {
    const fetchNavigationLanguages = async (): Promise<void> => {
      setHeaderDataLoading(true);
      try {
        const { data: responseData } = await axios.get(`${apiUrls.TC_PLUS_LANGUAGES}/${template.id}/language`);
        if (responseData) {
          updateHeaderDetails({
            ...headerDetails,
            langGroups: !ifErrorPage()
              ? responseData
              : responseData.filter(
                  (langObj: HeaderLangGroupProps) => langObj.locale.toUpperCase() === getCurrentLocale().toUpperCase(),
                ),
            showDetails: !!(expCookie && expCookie.showUserProfile),
            isICPEnabled: window.location.hostname === EXPERIENCE_HOST_DOMAINS.cn1,
          });
        } else {
          console.log('Error fetching navigation languages');
        }
      } catch (e) {
        console.log('Error fetching navigation languages', e);
      } finally {
        setHeaderDataLoading(false);
      }
    };

    if (tcPlusFlow && template.id && headerDetails.langGroups && !headerDetails.langGroups.length) {
      fetchNavigationLanguages();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tcPlusFlow, template.id]);

  /** Start: Setting Menu List handlers */
  const settingsAnchorRef = useRef<HTMLButtonElement>(null);
  const [openSettings, setOpenSettings] = useState(false);

  const handleToggleSettings = (languages: HeaderLangGroupProps[]): void => {
    if (languages?.length > 1) setOpenSettings((prevOpen) => !prevOpen);
  };

  const handleCloseSettings = (event: React.MouseEvent<Document, MouseEvent>): void => {
    if (null !== settingsAnchorRef.current && settingsAnchorRef.current.contains(event.currentTarget)) {
      return;
    }
    setOpenSettings(false);
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpenSettings = useRef(openSettings);
  useEffect(() => {
    if (prevOpenSettings.current === true && openSettings === false && null !== settingsAnchorRef.current) {
      settingsAnchorRef.current.focus();
    }

    prevOpenSettings.current = openSettings;
  }, [openSettings]);
  /** End: Setting Menu List handlers */

  /** Start: User Profile Menu List handlers */
  const userProfileAnchorRef = useRef<HTMLButtonElement>(null);
  const [openUserProfile, setOpenUserProfile] = useState(false);

  const handleToggleUserProfile = (): void => {
    setOpenUserProfile((prevOpen) => !prevOpen);
  };

  const handleCloseUserProfile = (event: React.MouseEvent<Document, MouseEvent>): void => {
    if (null !== userProfileAnchorRef.current && userProfileAnchorRef.current.contains(event.currentTarget)) {
      return;
    }
    setOpenUserProfile(false);
  };

  const handleUserProfileKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpenUserProfile(false);
    }
  };

  const getCurrentLanguage = (langGroup: ReadonlyArray<HeaderLangGroupProps> | undefined): string => {
    const currentLocale = getCurrentLocale();
    if (!langGroup || langGroup.length === 0) return t('english');
    const currentLangData = langGroup.filter((langObj) => langObj.locale.toUpperCase() === currentLocale.toUpperCase());
    if (currentLangData.length > 0) return currentLangData[0].languageName;
    return t('english');
  };

  // return focus to the button when we transitioned from !open -> open
  const prevOpenUserProfile = useRef(openUserProfile);
  useEffect(() => {
    if (prevOpenUserProfile.current === true && openUserProfile === false && null !== userProfileAnchorRef.current) {
      userProfileAnchorRef.current.focus();
    }

    prevOpenUserProfile.current = openUserProfile;
  }, [openUserProfile]);
  /** End: User Profile Menu List handlers */

  /** Menu Bar Handler --- shows up in tablet and mobile only */
  const toggleMenuDrawer = (open: boolean) => (event: React.KeyboardEvent | React.MouseEvent): void => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }
    setIsMenuOpen(open);
  };

  /** Language Change Handler */
  const onLanguageChange = (): void => {
    setOpenSettings(false);
  };

  /** signin click handler */
  const onSigninClick = (event: React.KeyboardEvent | React.MouseEvent): void => {
    if (event.type === 'keyup') {
      return;
    }
    if (
      event.type === 'keydown' &&
      !((event as React.KeyboardEvent).keyCode === 13 || (event as React.KeyboardEvent).keyCode === 32)
    ) {
      return;
    }
    event.preventDefault();
    setIsMenuOpen(false);
    history.push(routeMappings['LOGIN']);
  };

  /** Redirect to user profile */
  const onUserProfileClick = (event: React.KeyboardEvent | React.MouseEvent): void => {
    if (event.type === 'keyup') {
      return;
    }
    if (
      event.type === 'keydown' &&
      !((event as React.KeyboardEvent).keyCode === 13 || (event as React.KeyboardEvent).keyCode === 32)
    ) {
      return;
    }
    event.preventDefault();
    setIsMenuOpen(false);
    setOpenUserProfile(false);
    history.push(routeMappings['USER_PROFILE']);
  };

  /** logout handler */
  const onLogoutClick = async (event: React.KeyboardEvent | React.MouseEvent): Promise<void> => {
    if (event.type === 'keyup') {
      return;
    }
    if (
      event.type === 'keydown' &&
      !((event as React.KeyboardEvent).keyCode === 13 || (event as React.KeyboardEvent).keyCode === 32)
    ) {
      return;
    }
    event.preventDefault();
    setIsMenuOpen(false);
    setOpenUserProfile(false);
    const res = await logoutUser(candidate);
    if (tcPlusFlow && res.data && res.data.Success) {
      if (parsedWorkflowSession.assessmentNonCompletionURL)
        window.open(parsedWorkflowSession.assessmentNonCompletionURL, '_self');
      else history.push(routeMappings['LOGIN']);
    }
  };

  const defaultLanguageDropdownCondition = !settings.mfsEnabled || (settings.mfsEnabled && isPathShl360);
  const shl360LanguageDropdownCondition = renderLanguageSelector && settings.mfsEnabled && !isPathShl360;

  const languageDropdownList = (languages: HeaderLangGroupProps[]): JSX.Element => {
    return (
      <Box ml={2}>
        <Tooltip title={getCurrentLanguage(languages)}>
          <span>
            <Button
              ref={settingsAnchorRef}
              className={utilityLinkClass.settingsIcon}
              aria-controls={openSettings ? 'language-list-grow' : undefined}
              aria-haspopup={languages?.length > 1}
              aria-disabled={languages?.length < 2}
              disabled={languages?.length < 2}
              onClick={(): void => handleToggleSettings(languages)}
              endIcon={languages?.length > 1 && <FontAwesomeIcon icon={faChevronDown} />}
              startIcon={
                <Icon>
                  <LanguageIcon className={utilityLinkClass.languageIcon} />
                </Icon>
              }>
              <Typography variant="srOnly">{t('language')}: </Typography>
              <Box pr={1} className={utilityLinkClass.languageTitle}>
                {getCurrentLanguage(languages)}
              </Box>
            </Button>
          </span>
        </Tooltip>
        <Popper
          open={openSettings}
          anchorEl={settingsAnchorRef.current}
          role="listbox"
          transition
          disablePortal
          placement="bottom">
          {({ TransitionProps, placement }): JSX.Element => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
              }}>
              <Paper>
                <ClickAwayListener onClickAway={handleCloseSettings}>
                  <Grid item className={classes.desktopMenuSettings}>
                    <Grid>
                      <Grid item xs={12}>
                        <InputLabel htmlFor="language" className="language-label" tabIndex={-1}>
                          <b>{t('language')}</b>
                        </InputLabel>
                      </Grid>
                      {headerDataLoading ? (
                        <CircularProgress size="small" />
                      ) : (
                        <Grid item xs={12}>
                          <LanguageDropdown languages={languages} onLanguageChange={onLanguageChange} />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>
    );
  };

  return (
    <AppBar color={'default'} position={'fixed'} className={classes.header}>
      <Box display="flex" alignItems="center" className={classes.mobile}>
        {/** mobile header */}
        {!lgUp ? (
          <MobileHeader
            userName={(candidate && candidate.firstName) || ''}
            isMenuOpen={isMenuOpen}
            deadlineDatePassed={deadlineDatePassed}
            isPreviewProject={isPreviewProject}
            toggleMenuDrawer={toggleMenuDrawer}
            setIsMenuOpen={setIsMenuOpen}
            header={headerDetails}
            settings={settings}
            basicFlow={basicFlow}
            companyLogo={companyLogo}
            userSessionActive={userSessionActive}
            renderLanguageSelector={renderLanguageSelector}
            onLanguageChange={onLanguageChange}
            onLogoutClick={onLogoutClick}
            onUserProfileClick={onUserProfileClick}
            onSignInClick={onSigninClick}
          />
        ) : (
          /** Desktop Header */
          <>
            <Box className={classes.logo}>
              {(settings.utilityBar.logo || basicFlow) && (
                <Logo src={companyLogo} alt={settings.utilityBar.logo?.name || ''} />
              )}
            </Box>

            {/** Template Utility links */}

            <Box display="flex" component="ul" className={utilityLinkClass.utilityLink} mr={6}>
              {links && getLinksJSX(links)}
            </Box>

            {userSessionActive ? (
              /** User Profile menu list in case user is logged in */
              <div>
                <Button
                  ref={userProfileAnchorRef}
                  disabled={deadlineDatePassed}
                  aria-hidden={deadlineDatePassed}
                  aria-disabled="false"
                  aria-controls={openUserProfile ? 'user-profile-list-grow' : undefined}
                  aria-haspopup="true"
                  color="inherit"
                  onClick={handleToggleUserProfile}
                  className={utilityLinkClass.userProfile}
                  startIcon={<FontAwesomeIcon icon={faUser} />}>
                  <Box pr={1}>{(candidate && candidate.firstName) || ''}</Box>
                </Button>

                <Popper
                  open={openUserProfile}
                  anchorEl={userProfileAnchorRef.current}
                  role="listbox"
                  placement="bottom-end"
                  transition
                  disablePortal>
                  {({ TransitionProps, placement }): JSX.Element => (
                    <Grow
                      {...TransitionProps}
                      style={{
                        transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
                      }}>
                      <Paper>
                        <ClickAwayListener onClickAway={handleCloseUserProfile}>
                          <MenuList
                            id="user-profile-list-grow"
                            onKeyDown={handleUserProfileKeyDown}
                            className={classes.desktopMenuUserProfile}>
                            {headerDetails.showDetails && !settings.mfsEnabled && (
                              <MenuItem onClick={onUserProfileClick} className={classes.dektopMenuItem}>
                                <b>{t('userProfile')}</b>
                              </MenuItem>
                            )}

                            <MenuItem
                              onClick={onLogoutClick}
                              disabled={isPreviewProject}
                              className={classes.dektopMenuItem}>
                              <b>{t('signout')}</b>
                            </MenuItem>
                          </MenuList>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </div>
            ) : !settings.mfsEnabled ? (
              /** Sign In with icon in case user not logged in */
              // disable sign in button on link invalid page
              <Button
                onClick={onSigninClick}
                color="inherit"
                disabled={deadlineDatePassed || ifErrorPage()}
                aria-hidden={deadlineDatePassed || ifErrorPage()}
                aria-disabled="false"
                className={utilityLinkClass.userProfile}
                startIcon={<FontAwesomeIcon icon={faUser} />}>
                <Box pr={1}>{t('signIn')}</Box>
              </Button>
            ) : null}

            {tcPlusFlow && basicFlow && <AccessibilityDropdown />}

            {/** Settings Icon with language selection in menu list */}
            {defaultLanguageDropdownCondition && languageDropdownList(headerDetails.langGroups)}
            {shl360LanguageDropdownCondition && languageDropdownList(languages)}
          </>
        )}
      </Box>
    </AppBar>
  );
};

export default SHLEHeader;
